import { useState, useEffect } from 'react';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow
} from './ui/table';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Loader2, Plus, Trash2, Key, Copy, AlertCircle } from 'lucide-react';
import { getUserApiKeys, createUserApiKey, deleteUserApiKey } from '../services/api';
import type { ApiKey, UserApiKeyResponse, CreateUserApiKeyResponse } from '../types';
import { formatDateTime } from '../lib/utils';
import { toast } from './ui/toast';
import { Alert, AlertDescription, AlertTitle } from './ui/alert';

export function UserApiKeys() {
    const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [isCreating, setIsCreating] = useState(false);
    const [newKeyName, setNewKeyName] = useState('');
    const [showKeyDialog, setShowKeyDialog] = useState(false);
    const [createdKey, setCreatedKey] = useState<string | null>(null);
    const [isDeleting, setIsDeleting] = useState<string | null>(null);
    const [showCreateForm, setShowCreateForm] = useState(false);

    useEffect(() => {
        fetchApiKeys();
    }, []);

    const fetchApiKeys = async () => {
        setLoading(true);
        try {
            const response = await getUserApiKeys() as UserApiKeyResponse;
            setApiKeys(response.api_keys || []);
        } catch (err: any) {
            setError(err.message || 'Failed to fetch API keys');
        } finally {
            setLoading(false);
        }
    };

    const handleCreateKey = async () => {
        if (!newKeyName.trim()) return;

        setIsCreating(true);
        try {
            const response = await createUserApiKey({ name: newKeyName }) as CreateUserApiKeyResponse;
            setCreatedKey(response.key);
            setShowKeyDialog(true);
            setNewKeyName('');
            setShowCreateForm(false);
            fetchApiKeys();
            toast({
                title: 'API Key Created',
                description: 'Your new API key has been generated.',
            });
        } catch (err: any) {
            toast({
                title: 'Error',
                description: err.message || 'Failed to create API key',
                variant: 'destructive',
            });
        } finally {
            setIsCreating(false);
        }
    };

    const handleDeleteKey = async (id: string) => {
        if (!confirm('Are you sure you want to delete this API key? This action cannot be undone.')) return;

        setIsDeleting(id);
        try {
            await deleteUserApiKey(id);
            setApiKeys(apiKeys.filter(key => key.id !== id));
            toast({
                title: 'API Key Deleted',
                description: 'The API key has been successfully removed.',
            });
        } catch (err: any) {
            toast({
                title: 'Error',
                description: err.message || 'Failed to delete API key',
                variant: 'destructive',
            });
        } finally {
            setIsDeleting(null);
        }
    };

    const copyToClipboard = (text: string) => {
        navigator.clipboard.writeText(text);
        toast({
            title: 'Copied',
            description: 'API key copied to clipboard',
        });
    };

    return (
        <div className="space-y-4">
            <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0">
                    <div>
                        <CardTitle>API Keys</CardTitle>
                        <CardDescription>
                            Generate API keys for programmatic access to the API
                        </CardDescription>
                    </div>
                    <Dialog open={showCreateForm} onOpenChange={setShowCreateForm}>
                        <DialogTrigger asChild>
                            <Button size="sm">
                                <Plus className="h-4 w-4 mr-2" />
                                New API Key
                            </Button>
                        </DialogTrigger>
                        <DialogContent>
                            <DialogHeader>
                                <DialogTitle>Create API Key</DialogTitle>
                                <DialogDescription>
                                    Give your API key a name to identify it later.
                                </DialogDescription>
                            </DialogHeader>
                            <div className="py-4">
                                <Input
                                    placeholder="Key Name (e.g. Development, Production)"
                                    value={newKeyName}
                                    onChange={(e) => setNewKeyName(e.target.value)}
                                    onKeyDown={(e) => e.key === 'Enter' && handleCreateKey()}
                                />
                            </div>
                            <DialogFooter>
                                <Button
                                    onClick={handleCreateKey}
                                    disabled={isCreating || !newKeyName.trim()}
                                >
                                    {isCreating && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                                    Create Key
                                </Button>
                            </DialogFooter>
                        </DialogContent>
                    </Dialog>
                </CardHeader>

                <CardContent>
                    {error && (
                        <Alert variant="destructive" className="mb-4">
                            <AlertCircle className="h-4 w-4" />
                            <AlertTitle>Error</AlertTitle>
                            <AlertDescription>{error}</AlertDescription>
                        </Alert>
                    )}

                    {loading ? (
                        <div className="flex justify-center py-8">
                            <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
                        </div>
                    ) : apiKeys.length === 0 ? (
                        <div className="text-center py-8 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-dashed">
                            <Key className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                            <p className="text-sm text-gray-600 dark:text-gray-400">No API keys found. Create one to get started.</p>
                        </div>
                    ) : (
                        <div className="border rounded-md overflow-hidden">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Name</TableHead>
                                        <TableHead>Created</TableHead>
                                        <TableHead>Last Used</TableHead>
                                        <TableHead className="w-[80px] text-right">Actions</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {apiKeys.map((key) => (
                                        <TableRow key={key.id}>
                                            <TableCell className="font-medium">{key.name}</TableCell>
                                            <TableCell className="text-gray-500 dark:text-gray-400 text-xs whitespace-nowrap">
                                                {formatDateTime(key.created_at)}
                                            </TableCell>
                                            <TableCell className="text-gray-500 dark:text-gray-400 text-xs whitespace-nowrap">
                                                {key.last_used_at ? formatDateTime(key.last_used_at) : 'Never'}
                                            </TableCell>
                                            <TableCell className="text-right">
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    className="text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                                                    disabled={isDeleting === key.id}
                                                    onClick={() => handleDeleteKey(key.id)}
                                                >
                                                    {isDeleting === key.id ? (
                                                        <Loader2 className="h-4 w-4 animate-spin" />
                                                    ) : (
                                                        <Trash2 className="h-4 w-4" />
                                                    )}
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>
                    )}
                </CardContent>
            </Card>

            <Dialog open={showKeyDialog} onOpenChange={setShowKeyDialog}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle>API Key Generated</DialogTitle>
                        <DialogDescription>
                            Please copy your API key now. For security reasons, it won't be shown again.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="flex items-center space-x-2 py-4">
                        <div className="grid flex-1 gap-2">
                            <Input
                                readOnly
                                value={createdKey || ''}
                                className="font-mono text-sm bg-gray-50 dark:bg-gray-900"
                            />
                        </div>
                        <Button
                            type="button"
                            size="sm"
                            className="px-3"
                            onClick={() => createdKey && copyToClipboard(createdKey)}
                        >
                            <span className="sr-only">Copy</span>
                            <Copy className="h-4 w-4" />
                        </Button>
                    </div>
                    <DialogFooter className="sm:justify-start">
                        <Button
                            type="button"
                            variant="secondary"
                            onClick={() => setShowKeyDialog(false)}
                        >
                            Close
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
